import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Gift, Share2, Copy, Check, Award, TrendingUp, Star } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import { TokenHandler } from '@/components/auth/TokenHandler';
import { useAuth } from '@/hooks/useAuth';
import apiClient, { ReferralSummary } from '@/lib/api-client';

export const Referrals: React.FC = () => {
  const { customer, isLoading: authLoading, error: authError } = useAuth();
  const [referralData, setReferralData] = useState<ReferralSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchReferralData = async () => {
      if (!apiClient.isAuthenticated()) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const data = await apiClient.getReferralSummary();
        setReferralData(data);
      } catch (err) {
        console.error('Error fetching referral data:', err);
        setError('Failed to load referral data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReferralData();
  }, []);

  const handleEnroll = async () => {
    try {
      setIsEnrolling(true);
      const result = await apiClient.enrollAsReferrer();
      if (result.success) {
        const updatedData = await apiClient.getReferralSummary();
        setReferralData(updatedData);
      } else {
        setError(result.error || 'Failed to enroll');
      }
    } catch (err) {
      console.error('Error enrolling as referrer:', err);
      setError('Failed to enroll in referral program');
    } finally {
      setIsEnrolling(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getReferralUrl = () => {
    if (!referralData?.referralCode) return '';
    // This would be the actual store URL in production
    return `https://store.example.com/?ref=${referralData.referralCode}`;
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  if (authLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">Loading referral program...</p>
        </div>
      </div>
    );
  }

  if (authError || error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card elevation="md" className="max-w-md">
          <CardBody>
            <div className="text-center">
              <p className="text-error-600 font-medium mb-4">{authError || error}</p>
              <p className="text-neutral-600 text-sm">
                Please try again or contact support.
              </p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!customer) {
    return (
      <>
        <TokenHandler />
        <div className="flex items-center justify-center min-h-screen">
          <Card elevation="md" className="max-w-md">
            <CardBody>
              <div className="text-center">
                <p className="text-neutral-900 font-medium mb-2">Welcome!</p>
                <p className="text-neutral-600 text-sm">
                  Please check your email for your magic link to access your dashboard.
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </>
    );
  }

  // Not enrolled state
  if (!referralData?.enrolled) {
    return (
      <>
        <TokenHandler />
        <div className="space-y-6">
          <motion.section
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-accent-500 to-accent-700 rounded-2xl p-6 md:p-8 text-white text-center"
          >
            <Users className="w-16 h-16 mx-auto mb-4" />
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">
              Share the Love & Earn Rewards
            </h1>
            <p className="text-accent-100 text-lg mb-6 max-w-xl mx-auto">
              Invite your friends and family to shop with us. When they make their first purchase,
              you both get rewarded with points and discounts!
            </p>

            <div className="bg-white/20 rounded-xl p-6 max-w-lg mx-auto mb-6">
              <h3 className="font-bold text-xl mb-4">How It Works</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <Share2 className="w-6 h-6" />
                  </div>
                  <p className="text-sm">Share your unique link</p>
                </div>
                <div>
                  <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <Gift className="w-6 h-6" />
                  </div>
                  <p className="text-sm">Friends get 10% off</p>
                </div>
                <div>
                  <div className="bg-white/20 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-2">
                    <Star className="w-6 h-6" />
                  </div>
                  <p className="text-sm">You earn points</p>
                </div>
              </div>
            </div>

            <Button
              variant="secondary"
              size="lg"
              onClick={handleEnroll}
              disabled={isEnrolling}
              className="bg-white text-accent-700 hover:bg-accent-50"
            >
              {isEnrolling ? 'Enrolling...' : 'Join the Referral Program'}
            </Button>
          </motion.section>
        </div>
      </>
    );
  }

  // Enrolled state
  return (
    <>
      <TokenHandler />
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="space-y-6"
      >
        {/* Referral Code Hero */}
        <motion.section
          variants={item}
          className="bg-gradient-to-br from-accent-500 to-accent-700 rounded-2xl p-6 md:p-8 text-white"
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-accent-100 text-sm mb-1">Your Referral Code</p>
              <h1 className="text-4xl md:text-5xl font-display font-bold tracking-wider">
                {referralData.referralCode}
              </h1>
            </div>
            <div className="bg-white/20 rounded-full p-4">
              <Users className="w-10 h-10 text-white" />
            </div>
          </div>

          {/* Share Link */}
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-accent-100 text-sm mb-2">Share your link</p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={getReferralUrl()}
                className="flex-1 bg-white/20 border border-white/30 rounded-lg px-4 py-2 text-white placeholder-white/50"
              />
              <Button
                variant="outline"
                onClick={() => copyToClipboard(getReferralUrl())}
                className="border-white text-white hover:bg-white/20 flex items-center gap-2"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          {/* Discount Code for Friends */}
          {referralData.discountCode && (
            <div className="mt-4 bg-white/10 rounded-xl p-4">
              <p className="text-accent-100 text-sm mb-2">Discount code for your friends</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold tracking-widest">{referralData.discountCode}</span>
                <Button
                  variant="ghost"
                  onClick={() => copyToClipboard(referralData.discountCode || '')}
                  className="text-white hover:bg-white/20"
                >
                  {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                </Button>
              </div>
              <p className="text-accent-200 text-xs mt-1">Friends get 10% off their first order</p>
            </div>
          )}
        </motion.section>

        {/* Stats Grid */}
        <motion.div variants={item} className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card padding="sm" variant="glass" elevation="sm">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary-600">{referralData.totalReferrals}</p>
              <p className="text-xs text-neutral-600">Total Referrals</p>
            </div>
          </Card>
          <Card padding="sm" variant="glass" elevation="sm">
            <div className="text-center">
              <p className="text-3xl font-bold text-success-600">{referralData.successfulReferrals || 0}</p>
              <p className="text-xs text-neutral-600">Successful</p>
            </div>
          </Card>
          <Card padding="sm" variant="glass" elevation="sm">
            <div className="text-center">
              <p className="text-3xl font-bold text-accent-600">{referralData.totalPointsEarned.toLocaleString()}</p>
              <p className="text-xs text-neutral-600">Points Earned</p>
            </div>
          </Card>
          <Card padding="sm" variant="glass" elevation="sm">
            <div className="text-center">
              <p className="text-3xl font-bold text-warning-600">
                ${(referralData.totalReferredRevenue || 0).toFixed(0)}
              </p>
              <p className="text-xs text-neutral-600">Revenue Generated</p>
            </div>
          </Card>
        </motion.div>

        {/* Points Breakdown */}
        <motion.div variants={item}>
          <Card elevation="md" gradient gradientDirection="cool">
            <CardHeader>
              <div className="flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-primary-600" />
                <CardTitle>Your Earnings</CardTitle>
              </div>
            </CardHeader>
            <CardBody>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <div className="bg-success-100 rounded-full p-2">
                      <Gift className="w-5 h-5 text-success-600" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-800">First Order Bonuses</p>
                      <p className="text-sm text-neutral-500">Points from friend's first orders</p>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-success-600">
                    +{(referralData.firstOrderPoints || 0).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <div className="bg-warning-100 rounded-full p-2">
                      <Award className="w-5 h-5 text-warning-600" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-800">Milestone Bonuses</p>
                      <p className="text-sm text-neutral-500">Extra points for achievements</p>
                    </div>
                  </div>
                  <span className="text-xl font-bold text-warning-600">
                    +{(referralData.milestonePoints || 0).toLocaleString()}
                  </span>
                </div>
              </div>
            </CardBody>
          </Card>
        </motion.div>

        {/* Milestones */}
        {referralData.milestones && referralData.milestones.length > 0 && (
          <motion.div variants={item}>
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6 text-warning-500" />
                  <CardTitle>Milestones Achieved</CardTitle>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {referralData.milestones.map((milestone, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-warning-50 rounded-xl border border-warning-200"
                    >
                      <div className="flex items-center gap-3">
                        <div className="bg-warning-500 rounded-full p-2">
                          <Star className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-neutral-800">
                            {milestone.milestoneType.replace(/_/g, ' ')}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {new Date(milestone.achievedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-warning-600">
                        +{milestone.pointsAwarded}
                      </span>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Recent Referrals */}
        {referralData.recentReferrals && referralData.recentReferrals.length > 0 && (
          <motion.div variants={item}>
            <Card elevation="md">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Users className="w-6 h-6 text-neutral-600" />
                  <CardTitle>Recent Referrals</CardTitle>
                </div>
              </CardHeader>
              <CardBody>
                <div className="space-y-3">
                  {referralData.recentReferrals.map((referral, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`rounded-full w-10 h-10 flex items-center justify-center ${
                          referral.hasPurchased ? 'bg-success-100' : 'bg-neutral-100'
                        }`}>
                          <span className="text-lg">
                            {referral.anonymizedName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-neutral-800">{referral.anonymizedName}</p>
                          <p className="text-xs text-neutral-500">
                            Referred {new Date(referral.referredAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        {referral.hasPurchased ? (
                          <span className="inline-flex items-center gap-1 text-sm text-success-600">
                            <Check className="w-4 h-4" />
                            Purchased
                          </span>
                        ) : (
                          <span className="text-sm text-neutral-500">Pending</span>
                        )}
                        {referral.pointsEarned > 0 && (
                          <p className="text-xs text-success-600 font-medium">
                            +{referral.pointsEarned} pts
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          </motion.div>
        )}

        {/* Share Options */}
        <motion.div variants={item}>
          <Card elevation="md">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Share2 className="w-6 h-6 text-primary-600" />
                <CardTitle>Share With Friends</CardTitle>
              </div>
            </CardHeader>
            <CardBody>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    const url = `https://wa.me/?text=${encodeURIComponent(`Use my code ${referralData.referralCode} to get 10% off your first order! ${getReferralUrl()}`)}`;
                    window.open(url, '_blank');
                  }}
                >
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getReferralUrl())}`;
                    window.open(url, '_blank');
                  }}
                >
                  Facebook
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(`Use my code ${referralData.referralCode} to get 10% off! ${getReferralUrl()}`)}`;
                    window.open(url, '_blank');
                  }}
                >
                  Twitter
                </Button>
                <Button
                  variant="outline"
                  fullWidth
                  onClick={() => {
                    const subject = `Check out this shop!`;
                    const body = `Hey! I thought you'd like this. Use my referral code ${referralData.referralCode} to get 10% off your first order!\n\n${getReferralUrl()}`;
                    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  }}
                >
                  Email
                </Button>
              </div>
            </CardBody>
          </Card>
        </motion.div>
      </motion.div>
    </>
  );
};

export default Referrals;
